import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import BasicTwoColumnData from '../../components/Details/BasicTwoColumnData';
import { ChangeLogType } from '../../types/types';
import { HiArrowLeft } from 'react-icons/hi';
import SectionHeader from '../../components/CoreComponents/Details/SectionHeader';
import { formatChangelogDateTime } from '../../utils/exportUtils';

export default function ChangeLogDetails() {
  const { changelogUuid } = useParams<{ changelogUuid: string }>();
  const navigate = useNavigate();

  const { ChangeLogs } = useSelector((state: RootState) => state.ChangeLogs);
  const { ChangeLogsTypes } = useSelector((state: RootState) => state.changelogsTypes);
  const { allDisciplines } = useSelector((state: RootState) => state.Discipline);
  const { projects } = useSelector((state: RootState) => state.Project);

  // Find the specific changelog
  const changelog = useMemo(() => {
    return ChangeLogs.find(log => log.changelogUuid === changelogUuid);
  }, [ChangeLogs, changelogUuid]);

  // Redirect if changelog not found
  useEffect(() => {
    if (!changelog && ChangeLogs.length > 0) {
      navigate('/ChangeLogs');
    }
  }, [changelog, ChangeLogs.length, navigate]);

  if (!changelog) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <p className="text-xl text-gray-600 dark:text-gray-400">Loading changelog...</p>
        </div>
      </div>
    );
  }

  // Get type name
  const typeName = ChangeLogsTypes.find((type: ChangeLogType) => type.id === changelog.changelogTypeId)?.typeName || 'Unknown';

  // Get discipline name
  const discipline = allDisciplines.find(d => String(d.disciplineId) === String(changelog.changedByDisciplineId));
  const disciplineName = discipline?.disciplineName || changelog.changedByDisciplineId;

  // Get project text
  const project = projects.find(p => String(p.projectNumber) === String(changelog.projectNumber));
  const projectText = project ? `${project.projectNumber} - ${project.projectName}` : changelog.projectNumber;

  // Prepare data for BasicTwoColumnData component
  const changelogData = [
    { label: 'Type', value: typeName },
    { label: 'Description', value: changelog.description || 'N/A' },
    { label: 'Changed By', value: changelog.changedBy },
    { label: 'Email', value: changelog.changedEmail },
    { label: 'Changed On', value: formatChangelogDateTime(changelog.changedOn) },
    { label: 'Project', value: projectText },
    { label: 'Discipline', value: disciplineName },
    { label: 'Punchlist UUID', value: changelog.punchlistUuid || 'N/A' },
    { label: 'CT UUID', value: changelog.ctUuid || 'N/A' },
    { label: 'Changelog UUID', value: changelog.changelogUuid },
  ];

  return (
    <div className="max-w-4xl p-4">
      {/* Header with back button */}
      <div className="flex justify-between items-center mb-6">
        <SectionHeader title="Changelog Details" />
        <button
          onClick={() => navigate('/ChangeLogs')}
          className="flex items-center gap-2 text-orange-1000 hover:text-orange-900 transition-colors"
        >
          <HiArrowLeft size={20} />
        </button>
      </div>

      {/* Changelog details */}
      <BasicTwoColumnData
        data={changelogData}
        labelClassName="font-semibold text-gray-700 dark:text-gray-300 min-w-[150px]"
        rowClassName="py-3 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
      />
    </div>
  );
}
